import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';

import {
  getTransferPayments,
  confirmTransferPayment,
  cancelPurchase,
} from '../services/payments';

const useStyles = makeStyles(theme => ({
  table: {
    marginTop: 10,
    padding: 5,
    '& .MuiTableCell-sizeSmall': {
      padding: theme.spacing(0.75),
    },
  },
  header: {
    background: theme.palette.primary.main,
  },
  headerCell: {
    color: theme.palette.common.white,
    fontWeight: 'bold',
  },
  noResultsBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    textAlign: 'center',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  formControl: { minWidth: 160 },
  grow: { flex: 1 },
}));

const formatCurrency = (value, locale = 'es-AR', currency = 'ARS') => {
  if (value === null || value === undefined || isNaN(Number(value))) return '-';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(Number(value));
  } catch {
    return `${currency} ${Number(value).toFixed(2)}`;
  }
};

const formatDate = iso => {
  if (!iso) return '-';
  const d = new Date(iso);
  // dd/mm/yyyy hh:mm
  const pad = n => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export default function TransfersTable() {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [refreshToken, setRefreshToken] = useState(0);

  const [pendingAction, setPendingAction] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const pageSizeOptions = [10, 25, 50, 100];

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 0;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const pageOptions = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => i + 1),
    [totalPages]
  );

  useEffect(() => {
    fetchData(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, refreshToken]);

  const fetchData = async (pageNumber = 1, perPage = 25) => {
    setLoading(true);
    try {
      const resp = await getTransferPayments(pageNumber, perPage);
      const data = resp?.data?.results ?? [];
      const count = resp?.data?.count ?? 0;
      setRows(Array.isArray(data) ? data : []);
      setTotal(Number(count) || 0);
    } catch (e) {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = e => setPage(Number(e.target.value));
  const handleChangePageSize = e => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };
  const handlePrev = () => setPage(p => Math.max(1, p - 1));
  const handleNext = () => setPage(p => Math.min(totalPages, p + 1));

  const openConfirm = item => setPendingAction({ type: 'confirm', item });
  const openCancel = item => setPendingAction({ type: 'cancel', item });
  const closeModal = () => !submitting && setPendingAction(null);

  const executeAction = async () => {
    if (!pendingAction) return;
    setSubmitting(true);
    const compraId = pendingAction.item?.id;
    try {
      if (pendingAction.type === 'confirm') {
        await confirmTransferPayment(compraId);
      } else {
        await cancelPurchase(compraId);
      }
      setPendingAction(null);
      setRefreshToken(t => t + 1)
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const headers = [
    'ID',
    'Fecha de creación',
    'Total (ARS)',
    'Perks',
    'Costos fijos',
    'Cliente',
    'Acciones',
  ];

  return (
    <>
      {loading ? (
        <Box mt={6} display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <Box p={4}>
          {rows?.length > 0 ? (
            <>
              <TableContainer component={Paper}>
                <Table
                  size="small"
                  aria-label="Pagos por transferencia (pendientes)"
                  className={classes.table}
                >
                  <TableHead>
                    <TableRow className={classes.header}>
                      {headers.map(h => (
                        <TableCell
                          key={h}
                          align="center"
                          className={classes.headerCell}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(item => {
                      const totalArs = formatCurrency(
                        item?.total_ars,
                        'es-AR',
                        'ARS'
                      );
                      const totalUsd = formatCurrency(
                        item?.total_usd,
                        'en-US',
                        'USD'
                      );
                      return (
                        <TableRow key={item.id} hover>
                          <TableCell align="center">
                            {item?.id ?? '-'}
                          </TableCell>
                          <TableCell align="center">
                            {formatDate(
                              item?.created_at || item?.fecha_creacion
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title={`Equivale a ${totalUsd}`} arrow>
                              <span>{totalArs}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center">
                            {item?.perks_chord &&
                            Object.keys(item?.perks_chord).length > 0
                              ? Object.entries(item?.perks_chord)
                                  .map(([key, value]) => `${key}: ${value} U$S`)
                                  .join(', ')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {item?.costos_fijos_chord &&
                            Object.keys(item?.costos_fijos_chord).length > 0
                              ? Object.entries(item?.costos_fijos_chord)
                                  .map(([key, value]) => `${key}: ${value} U$S`)
                                  .join(', ')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            {item?.cliente_email || item?.email || '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Confirmar pago">
                              <span>
                                <IconButton
                                  aria-label="Confirmar pago"
                                  onClick={() => openConfirm(item)}
                                  disabled={submitting}
                                  size="small"
                                >
                                  <DoneAllIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cancelar compra">
                              <span>
                                <IconButton
                                  aria-label="Cancelar compra"
                                  onClick={() => openCancel(item)}
                                  disabled={submitting}
                                  size="small"
                                >
                                  <CancelIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box className={classes.toolbar}>
                <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControl}
                >
                  <InputLabel id="page-size-label">
                    Cantidad
                  </InputLabel>
                  <Select
                    labelId="page-size-label"
                    value={pageSize}
                    onChange={handleChangePageSize}
                    label="Cantidad por página"
                  >
                    {pageSizeOptions.map(opt => (
                      <MenuItem key={opt} value={opt}>
                        {opt}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl
                  variant="outlined"
                  size="small"
                  className={classes.formControl}
                  disabled={totalPages === 0}
                >
                  <InputLabel id="page-label">Página</InputLabel>
                  <Select
                    labelId="page-label"
                    value={Math.min(page, totalPages || 1)}
                    onChange={handleChangePage}
                    label="Página"
                  >
                    {pageOptions.map(p => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <IconButton
                  onClick={handlePrev}
                  disabled={page <= 1}
                  aria-label="Anterior"
                >
                  <ChevronLeftIcon />
                </IconButton>
                <IconButton
                  onClick={handleNext}
                  disabled={page >= totalPages}
                  aria-label="Siguiente"
                >
                  <ChevronRightIcon />
                </IconButton>

                <Box className={classes.grow} />
                <Box>
                  Página {page} de {totalPages || 1} — Total: {total}
                </Box>
              </Box>
            </>
          ) : (
            <Box className={classes.noResultsBox}>No hay resultados.</Box>
          )}
        </Box>
      )}

      <Dialog
        open={!!pendingAction}
        onClose={closeModal}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {pendingAction?.type === 'confirm'
            ? 'Confirmar pago'
            : 'Cancelar compra'}
        </DialogTitle>
        <DialogContent dividers>
          {pendingAction?.type === 'confirm'
            ? `Vas a confirmar el pago de la compra #${pendingAction?.item?.id}. ¿Querés continuar?`
            : `Vas a cancelar la compra #${pendingAction?.item?.id}. ¿Querés continuar?`}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={submitting}>
            Volver
          </Button>
          <Button
            onClick={executeAction}
            color={pendingAction?.type === 'confirm' ? 'primary' : 'secondary'}
            variant="contained"
            disabled={submitting}
          >
            {pendingAction?.type === 'confirm'
              ? 'Confirmar'
              : 'Cancelar compra'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
